import React, { useEffect, useState, useCallback } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { endpoints } from "../_config";
import { get } from "../_services/apiService";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const monthOrder = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// Utility to map API data to monthly totals
const getMonthlyTotals = (arr) => {
  const monthMap = Object.fromEntries(arr.map(item => [item.month, item.total]));
  return monthOrder.map(month => monthMap[month] ?? 0);
};

// Reusable Chart Component
const ChartCard = ({ title, color, data }) => {
  const chartData = {
    labels: monthOrder,
    datasets: [
      {
        label: title,
        data: getMonthlyTotals(data),
        fill: false,
        borderColor: color,
        backgroundColor: color,
        tension: 0.1,
        pointBackgroundColor: color,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true } },
  };

  return (
    <div className="col-lg-6 mt-4">
      <div className="dashboard-line-charts card p-3">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

const PieChartCard = ({ title, data }) => {
  const chartData = {
    labels: data.map(item => item.service),
    datasets: [
      {
        label: title,
        data: data.map(item => item.total),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)"
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)"
        ],
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="col-lg-6 mt-4">
      <div className="dashboard-pie-charts card p-3 text-center">
        <div style={{ width: "550px", height: "350px" }}>
          <Pie 
            data={chartData} 
            options={{
              maintainAspectRatio: false, // lets chart stretch to container
            }} 
          />
        </div>
      </div>
    </div>
  );
};

const monthColors = [
    "rgba(255, 99, 132, 0.6)",  // Jan
    "rgba(54, 162, 235, 0.6)",  // Feb
    "rgba(255, 206, 86, 0.6)",  // Mar
    "rgba(75, 192, 192, 0.6)",  // Apr
    "rgba(153, 102, 255, 0.6)", // May
    "rgba(255, 159, 64, 0.6)",  // Jun
    "rgba(199, 199, 199, 0.6)", // Jul
    "rgba(255, 99, 71, 0.6)",   // Aug
    "rgba(0, 255, 127, 0.6)",   // Sep
    "rgba(255, 215, 0, 0.6)",   // Oct
    "rgba(70, 130, 180, 0.6)",  // Nov
    "rgba(255, 105, 180, 0.6)", // Dec
  ];
// Bar Chart Card for Club Membership
const BarChartCard = ({ title, data }) => {
  const chartData = {
    labels: monthOrder,
    datasets: [
      {
        label: title,
        data: getMonthlyTotals(data),
        backgroundColor:  monthColors,
        borderColor: monthColors.map(c => c.replace("0.6", "1")),
        borderWidth: 1,
        borderRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          autoSkip: false, // show all labels
          maxRotation: 0,  // keep horizontal or set 45 for angled
          minRotation: 0,
        },
      },
    },
  };

  return (
    <div className="col-lg-6 mt-4">
      <div className="dashboard-bar-charts card p-3">
        <div style={{ width: "100%", height: "400px" }}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};


const DashboardLineChart = () => {
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [quotationsData, setQuotationsData] = useState([]);
  const [enquiryServiceData, setEnquiryServiceData] = useState([]);
  const [clubMembershipData, setClubMembershipData] = useState([]);

  const [error, setError] = useState("");

  const fetchDashboardData = useCallback(async () => {
    try {
      setError("");
      const res = await get(endpoints.GetDashboardData); 
      if (res?.isSuccess === 200) { 
        setAppointmentsData(res.data.appointments || []);
        setQuotationsData(res.data.quotations || []);
        setEnquiryServiceData(res.data.enquiries_by_services || []);
        setClubMembershipData(res.data.clubmembership || []);
      } else {
        setAppointmentsData([]);
        setQuotationsData([]);
        setEnquiryServiceData([]);
        setClubMembershipData([]);
      }
    } catch (err) {
      console.error(err);
      setError("Please try again later.");
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="row">
      {error && <p style={{ color: "red" }}>{error}</p>}
      <ChartCard title="Appointments" color="rgb(75, 192, 192)" data={appointmentsData} />
      <ChartCard title="Quotations" color="rgb(255, 99, 132)" data={quotationsData} />
       <PieChartCard title="Enquiries by Service" data={enquiryServiceData} />
       <BarChartCard title="Club Membership" data={clubMembershipData} />
    </div>
  );
};

export default DashboardLineChart;
